"use client";

import { onAuthStateChange } from "@/lib/firebase-auth";
import { createUserProfile, getUser } from "@/lib/users";
import { usernameFromEmail } from "@/lib/utils";
import { User } from "@/types";
import { User as FirebaseUser } from "firebase/auth";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  refreshUser: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Track if user was previously authenticated to handle sign-out cookie cleanup
  const hadUserRef = React.useRef(false);

  const refreshUser = async () => {
    const firebaseUser = (await import("@/lib/firebase")).auth.currentUser;
    if (firebaseUser) {
      const userData = await getUser(firebaseUser.uid);
      setUser(userData);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let isCleaningUp = false;

    const unsubscribe = onAuthStateChange(
      async (firebaseUser: FirebaseUser | null) => {
        // Skip if cleaning up
        if (isCleaningUp || !isMounted) return;

        if (firebaseUser) {
          // Refresh and set the auth cookie
          try {
            const idToken = await firebaseUser.getIdToken();
            if (idToken && isMounted && !isCleaningUp) {
              const response = await fetch("/api/auth/session", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ idToken }),
              });

              // Only log errors if the request actually failed (not just network issues during cleanup)
              if (!response.ok && isMounted && !isCleaningUp) {
                console.error(
                  "Failed to set auth cookie:",
                  response.statusText,
                );
              }
            }
          } catch (error) {
            // Only log if not cleaning up and still mounted
            if (isMounted && !isCleaningUp) {
              console.error("Error refreshing auth cookie:", error);
            }
          }

          // Fetch user data from Firestore (both public and private)
          let userData = await getUser(firebaseUser.uid);

          // If user doesn't exist in Firestore, create profile
          if (!userData && isMounted && !isCleaningUp) {
            await createUserProfile(
              firebaseUser.uid,
              firebaseUser.email || "",
              firebaseUser.displayName ||
                usernameFromEmail(firebaseUser.email) ||
                "User",
              firebaseUser.photoURL || "",
            );
            userData = await getUser(firebaseUser.uid);
          }

          // Only update state if component is still mounted and not cleaning up
          if (isMounted && !isCleaningUp) {
            setUser(userData);
            hadUserRef.current = true;
          }
        } else {
          // Clear the auth cookie when user signs out or session expires
          // Only clear if there was a previously authenticated user and we're not cleaning up
          if (hadUserRef.current && isMounted && !isCleaningUp) {
            try {
              await fetch("/api/auth/session", {
                method: "DELETE",
              });
            } catch (error) {
              // Silently fail during cleanup - cookie will expire anyway
              if (isMounted && !isCleaningUp) {
                console.error("Error clearing auth cookie:", error);
              }
            }
          }

          // Only update state if component is still mounted and not cleaning up
          if (isMounted && !isCleaningUp) {
            setUser(null);
            hadUserRef.current = false;
          }
        }

        if (isMounted && !isCleaningUp) {
          setLoading(false);
        }
      },
    );

    return () => {
      isCleaningUp = true;
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Refresh token every 50 minutes (tokens expire after 1 hour)
  // Use ref to track active state to prevent race conditions with async operations
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshActiveRef = useRef(false);

  // Shared token refresh function
  const refreshToken = async (forceRefresh = false) => {
    // Check ref at the start - if not active, bail immediately
    if (!isRefreshActiveRef.current) return;

    try {
      const firebaseUser = (await import("@/lib/firebase")).auth.currentUser;

      // Check again after async import
      if (!firebaseUser || !isRefreshActiveRef.current) return;

      const idToken = await firebaseUser.getIdToken(forceRefresh);

      // Check again after token fetch
      if (!idToken || !isRefreshActiveRef.current) return;

      const response = await fetch("/api/auth/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });

      // Only log if still active
      if (!response.ok && isRefreshActiveRef.current) {
        console.error("Failed to refresh auth session:", response.statusText);
      }
    } catch (error) {
      // Only log if interval is still active
      if (isRefreshActiveRef.current) {
        console.error("Error refreshing token:", error);
      }
    }
  };

  useEffect(() => {
    if (!user) {
      // Clear any existing interval when user logs out
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      isRefreshActiveRef.current = false;
      return;
    }

    isRefreshActiveRef.current = true;

    refreshIntervalRef.current = setInterval(
      () => refreshToken(true),
      50 * 60 * 1000,
    ); // 50 minutes

    return () => {
      // Set inactive BEFORE clearing interval to prevent race conditions
      isRefreshActiveRef.current = false;
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [user]);

  // Refresh session cookie when app becomes visible (critical for PWA)
  // This handles cases where the app was backgrounded for a long time
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Force refresh token when app comes back to foreground
        // This ensures the server-side cookie is updated before any navigation
        refreshToken(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
